import {useEffect, useState} from 'react'
import {Link, useNavigate, useParams} from 'react-router-dom'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {initialValues} from './_module'
import clsx from 'clsx'
import {useTranslation} from 'react-i18next'

import {useAppSelector, useAppDispatch} from '../../../../redux/hooks'
import {getUserById, updateUser, reset} from '../../../../redux/Slice/userSlice'
import {getRole} from '../../../../redux/Slice/roleSlice'
import {decryptId} from '_metronic/helpers/Encrypt'
import {showErrorMessage, showSuccessMessage} from '_metronic/helpers/swalHelper'
import CustomSelect from 'app/custom/CustomSelect'
import Loader from 'app/custom/loader/Loader'

const EditUser = () => {
  const {id} = useParams()
  const userId = decryptId(id) || ''

  const {t} = useTranslation()
  const [loading, setLoading] = useState(false)
  const [loader, setLoader] = useState(true)
  const [allRoles, setAllRoles] = useState<any>([])
  const [oldRoles, setOldRoles] = useState<any>([])
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const {errors} = useAppSelector((state) => state.user)

  useEffect(() => {
    dispatch(getRole()).then((res) => {
      if (res.meta.requestStatus === 'fulfilled') {
        setLoader(false)
        setAllRoles(res.payload)
      }
    })

    if (userId) {
      dispatch(getUserById(userId)).then((res: any) => {
        if (res.meta.requestStatus === 'fulfilled') {
          const user = res.payload.user
          const userRoles = res.payload.user_roles
          formik.setFieldValue('name', user.name)
          formik.setFieldValue('username', user.username)
          formik.setFieldValue('email', user.email)
          formik.setFieldValue('rank_id', user.rank_id)
          formik.setFieldValue('position', user.position)
          const roleIds = userRoles.map((role: any) => role.id)
          formik.setFieldValue('roles', roleIds)
          setLoader(false)
        }
      })
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  // Form Validation
  const userSchema = Yup.object().shape({
    name: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.NAME')})),
    username: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.USERNAME')})),
    position: Yup.string().required(t('VALIDATION.REQUIRED_', {name: t('GLOBAL.POSITION')})),
    email: Yup.string()
      .email(t('PROFILE.EMAIL_FORMAT'))
      .required(t('VALIDATION.REQUIRED_', {name: t('PROFILE.EMAIL')})),
    password: Yup.string()
      .min(6, t('VALIDATION.MIN_CHARACTER_', {quantity: 6}))
      .max(50, t('VALIDATION.MAX_CHARACTER_', {quantity: 50})),
    password_confirmation: Yup.string()
      .min(6, t('VALIDATION.MIN_CHARACTER_', {quantity: 6}))
      .max(50, t('VALIDATION.MAX_CHARACTER_', {quantity: 50}))
      .oneOf(
        [Yup.ref('password')],
        t('VALIDATION.NO_MATCH_', {name: t('GLOBAL.CONFIRM_PASSWORD')})
      ),
    roles: Yup.array()
      .min(1, t('VALIDATION.REQUIRED_', {name: t('MENU.ROLES')}))
      .of(Yup.string()),
  })

  const formik = useFormik({
    initialValues,
    validationSchema: userSchema,
    onSubmit: (values, {setSubmitting, resetForm}) => {
      setLoading(true)
      const formData = new FormData()
      const roles = values.roles

      formData.append('user_id', userId)
      formData.append('name', values.name)
      formData.append('username', values.username)
      formData.append('email', values.email)
      formData.append('position', values.position)
      formData.append('password', values.password)
      formData.append('password_confirmation', values.password_confirmation)

      roles.forEach((item: any) => {
        formData.append('roles[]', item)
      })

      // Update User
      dispatch(updateUser(formData)).then((res: any) => {
        if (res.meta.requestStatus === 'fulfilled') {
          resetForm()
          dispatch(reset())
          showSuccessMessage(t('MESSAGES.UPDATED'), () => {
            navigate(-1)
          })
        } else if (res.meta.requestStatus === 'rejected') {
          showErrorMessage(t('MESSAGES.SERVER_ERROR'))
        }
        setLoading(false)
        setSubmitting(false)
      })
    },
  })

  return (
    <div className='card mb-5 mb-xl-10 mt-0'>
      <div className='card-header'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder'>{t('BUTTONS.EDIT_', {name: t('LABEL.USER')})}</h3>
        </div>
      </div>
      <div className='card-body p-9'>
        {!loader && (
          <form
            className='form w-100 fv-plugins-bootstrap5 fv-plugins-framework'
            noValidate
            autoComplete='off'
            onSubmit={formik.handleSubmit}
          >
            {/* First row */}
            <div className='row mb-8 col-12 shadow-sm m-2 p-5'>
              <div className='col-lg-4 col-md-4 col-sm-12'>
                <label className='col-form-label label fw-bolder required'>
                  {t('GLOBAL.NAME')}
                </label>
                <input
                  type='text'
                  placeholder={t('GLOBAL.NAME')}
                  {...formik.getFieldProps('name')}
                  className={clsx(
                    'form-control form-control-sm',
                    {
                      'is-invalid': formik.touched.name && formik.errors.name,
                    },
                    {
                      'is-valid': formik.touched.name && !formik.errors.name,
                    }
                  )}
                />
                {formik.touched.name && formik.errors.name && (
                  <span className='text-danger fw-bold'>{formik.errors.name}</span>
                )}
              </div>

              <div className='col-lg-4 col-md-4 col-sm-12'>
                <label className='col-form-label label fw-bolder required'>
                  {t('GLOBAL.USERNAME')}
                </label>
                <input
                  type='text'
                  placeholder={t('GLOBAL.USERNAME')}
                  {...formik.getFieldProps('username')}
                  className={clsx(
                    'form-control form-control-sm',
                    {
                      'is-invalid':
                        (formik.touched.username && formik.errors.username) || errors.username,
                    },
                    {
                      'is-valid': formik.touched.username && !formik.errors.username,
                    }
                  )}
                />
                {formik.touched.username && formik.errors.username && (
                  <span className='text-danger fw-bold'>{formik.errors.username}</span>
                )}
                {errors.username && (
                  <span className='text-danger fw-bold'>
                    {t('VALIDATION.DUPLICATED RECORD', {
                      name: t('LABELS.NAME', {name: t('USERS.USER')}),
                    })}
                  </span>
                )}
              </div>

              <div className='col-lg-4 col-md-4 col-sm-12'>
                <label className='col-form-label label fw-bolder required'>
                  {t('PROFILE.EMAIL')}
                </label>
                <input
                  type='text'
                  placeholder={t('PROFILE.EMAIL')}
                  {...formik.getFieldProps('email')}
                  className={clsx(
                    'form-control form-control-sm',
                    {
                      'is-invalid': (formik.touched.email && formik.errors.email) || errors.email,
                    },
                    {
                      'is-valid': formik.touched.email && !formik.errors.email,
                    }
                  )}
                />
                {formik.touched.email && formik.errors.email && (
                  <span className='text-danger fw-bold'>{formik.errors.email}</span>
                )}
                {errors.email && (
                  <span className='text-danger fw-bold'>
                    {t('VALIDATION.DUPLICATED RECORD', {
                      name: t('USERS.EMAIL'),
                    })}
                  </span>
                )}
              </div>

              <div className='col-lg-4 col-md-4 col-sm-12'>
                <label className='col-form-label label fw-bolder'>{t('GLOBAL.PASSWORD')}</label>
                <input
                  type='password'
                  placeholder={t('GLOBAL.PASSWORD')}
                  {...formik.getFieldProps('password')}
                  className={clsx(
                    'form-control form-control-sm',
                    {
                      'is-invalid': formik.touched.password && formik.errors.password,
                    },
                    {
                      'is-valid': formik.touched.password && !formik.errors.password,
                    }
                  )}
                />
                {formik.touched.password && formik.errors.password && (
                  <span className='text-danger fw-bold'>{formik.errors.password}</span>
                )}
              </div>

              <div className='col-lg-4 col-md-4 col-sm-12'>
                <label className='col-form-label label fw-bolder'>
                  {t('GLOBAL.CONFIRM_PASSWORD')}
                </label>
                <input
                  type='password'
                  placeholder={t('GLOBAL.CONFIRM_PASSWORD')}
                  {...formik.getFieldProps('password_confirmation')}
                  className={clsx(
                    'form-control form-control-sm',
                    {
                      'is-invalid':
                        formik.touched.password_confirmation && formik.errors.password_confirmation,
                    },
                    {
                      'is-valid':
                        formik.touched.password_confirmation &&
                        !formik.errors.password_confirmation,
                    }
                  )}
                />
                {formik.touched.password_confirmation && formik.errors.password_confirmation && (
                  <span className='text-danger fw-bold'>{formik.errors.password_confirmation}</span>
                )}
              </div>

              <div className='col-lg-4 col-md-4 col-sm-12'>
                <label className='col-form-label label fw-bolder required'>
                  {t('GLOBAL.POSITION')}
                </label>
                <input
                  type='text'
                  placeholder={t('GLOBAL.POSITION')}
                  {...formik.getFieldProps('position')}
                  className={clsx(
                    'form-control form-control-sm',
                    {
                      'is-invalid':
                        (formik.touched.position && formik.errors.position) || errors.position,
                    },
                    {
                      'is-valid': formik.touched.position && !formik.errors.position,
                    }
                  )}
                />
                {formik.touched.position && formik.errors.position && (
                  <span className='text-danger fw-bold'>{formik.errors.position}</span>
                )}
              </div>
              <div className='col-12'>
                <label className='col-form-label label fw-bolder required'>{t('MENU.ROLES')}</label>
                <CustomSelect
                  name='roles'
                  className='input'
                  value={allRoles.filter((role: any) =>
                    Array.isArray(formik.values.roles)
                      ? formik.values.roles.includes(role.id)
                      : false
                  )}
                  onChange={(roles: any) => {
                    formik.setFieldValue(
                      'roles',
                      roles.map((item: any) => item.id)
                    )
                  }}
                  defaultValues={allRoles.filter((role: any) =>
                    (Array.isArray(oldRoles) ? oldRoles.map((item: any) => item.id) : []).includes(
                      role.id
                    )
                  )}
                  options={allRoles}
                  placeholderText={t('MENU.ROLES')}
                  isMulti={true}
                />
                {formik.touched.roles && formik.errors.roles && (
                  <span className='text-danger fw-bold'>{formik.errors.roles}</span>
                )}
                {errors.roles && (
                  <span className='text-danger fw-bold'>
                    {t('VALIDATION.SELECT', {name: t('MENU.ROLES')})}
                  </span>
                )}
              </div>
            </div>

            <div className='row mb-8 col-12 shadow-sm m-2 p-5'>
              <div className='d-flex'>
                <div className='fs-6 fw-bold text-gray-700'>
                  <button
                    type='submit'
                    className='btn btn-sm btn-light btn-active-primary fw-bolder'
                    disabled={formik.isSubmitting || loading}
                  >
                    {!loading && (
                      <span className='indicator-label'>
                        <i className='fas fa-save mx-2'></i>
                        {t('BUTTONS.UPDATE')}
                      </span>
                    )}
                    {loading && (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        {t('GLOBAL.LOADING')}
                        <span className='spinner-border spinner-border-sm align-middle mx-2'></span>
                      </span>
                    )}
                  </button>
                  <Link
                    className='btn btn-sm btn-flex btn-light btn-active-dark fw-bolder mx-2'
                    to='/users-list'
                    onClick={() => dispatch(reset())}
                  >
                    <b>
                      <i className='fa-solid fa-circle-left mx-2'></i>
                      {t('BUTTONS.BACK')}
                    </b>
                  </Link>
                  <div />
                </div>
              </div>
            </div>
          </form>
        )}
        {loader && <Loader />}
      </div>
    </div>
  )
}

export default EditUser
